import { memo, useMemo, type ReactNode } from 'react';
import { Flex } from '@mantine/core';
import { IconLivePhoto } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { match, P } from 'ts-pattern';

import { Text } from '~/domains/design-system/Text';
import { isPublicGameWithTeams } from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import { formatEventDate } from '~/domains/market/utils/formatEventDate';
import { getGameLine } from '~/domains/market/utils/gameLine';
import {
  useMarketEvent,
  useMarketEventStart,
} from '~/domains/market/state/selectors/marketCard.selectors';

import { useStyles } from './MarketCard.styles';

type MarketCardGameStatusProps = {
  marketGroupId: string;
};

type EventStartLineProps = {
  eventStart: number | undefined;
};

const EventStartLine = memo(({ eventStart }: EventStartLineProps) => (
  <Text variant="subhead-small" c="gray2.3" transform="uppercase">
    {eventStart ? formatEventDate(eventStart) : '—'}
  </Text>
));

EventStartLine.displayName = 'EventStartLine';

function MarketCardGameStatusBase({ marketGroupId }: MarketCardGameStatusProps) {
  const { theme } = useStyles();
  const { t } = useTranslation('market');

  const eventStart = useMarketEventStart(marketGroupId);
  const event = useMarketEvent(marketGroupId);

  const rendersFromEventStart =
    !event || !isPublicGameWithTeams(event) || event.status === 'SCHEDULED';

  const gameLine = useMemo(
    () => (rendersFromEventStart ? null : getGameLine(event, Date.now())),
    [event, rendersFromEventStart]
  );

  if (!gameLine) {
    return <EventStartLine eventStart={eventStart} />;
  }

  return match(gameLine)
    .returnType<ReactNode>()
    .with({ isLive: true }, ({ statusLine }) => (
      <Flex align="center" gap={theme.other.spacing._4}>
        <IconLivePhoto size={14} color={theme.colors.red[5]} />
        <Text variant="subhead-small" c="red.5" transform="uppercase">
          {statusLine ?? t('card.live')}
        </Text>
      </Flex>
    ))
    .with({ isScheduled: false, statusLine: P.string }, ({ statusLine, dateLine }) => (
      <Flex align="center" gap={theme.other.spacing._4}>
        <Text variant="subhead-small" c="gray2.3" transform="uppercase">
          {statusLine}
        </Text>
        {!!dateLine && (
          <Text variant="subhead-small" c="gray2.3" transform="uppercase">
            {dateLine}
          </Text>
        )}
      </Flex>
    ))
    .otherwise(() => <EventStartLine eventStart={eventStart} />);
}

export const MarketCardGameStatus = memo(MarketCardGameStatusBase);

MarketCardGameStatus.displayName = 'MarketCardGameStatus';
